import React, { useState } from "react";
import { HeaderAction } from "../components/HeaderAction";
import {
  Box,
  Title,
  Button,
  Modal,
  TextInput,
  Flex,
  ActionIcon,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { TeamDto, UserDto } from "../dto/user";
import { useApi } from "../useApi";
import { getUsers, getTeams, createTeam, deleteTeam } from "../apiRoutes";
import { ColumnDef, sortingFns } from "@tanstack/react-table";
import TableInit from "../components/table/TableInit";
import { IconArchive, IconCheck, IconTrash, IconPlus } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";

const columnsCustomers: ColumnDef<UserDto>[] = [
  {
    accessorKey: "code",
    header: "Code",
    cell: (info) => info.getValue(),
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
  {
    accessorKey: "username",
    header: "Nom / Prénom",
    cell: (info) => info.getValue(),
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
  {
    accessorKey: "processingLevel",
    header: "Niveau de traitement",
    cell: (info) => info.getValue(),
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
  {
    accessorKey: "places",
    header: "Lieux",
    cell: ({ row }) => row.original.places?.split("§").join(", "),
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
  {
    accessorKey: "comment",
    header: "Commentaire",
    cell: (info) => info.getValue(),
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
  {
    accessorKey: "priority",
    header: "Prioritaire",
    cell: (info) =>
      info.getValue() ? <IconCheck height={17} color="#333333" /> : null,
    sortingFn: sortingFns.basic,
    enableColumnFilter: false,
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => (
      <span>
        {row.original.email}
        <br />
        {row.original.secoundEmail}
      </span>
    ),
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
];

const columnsRedactors: ColumnDef<UserDto>[] = [
  {
    accessorKey: "code",
    header: "Code",
    cell: (info) => info.getValue(),
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
  {
    accessorKey: "activated",
    header: "Activé",
    cell: (info) =>
      info.getValue() ? (
        <IconCheck height={17} color="#333333" />
      ) : (
        <IconArchive height={17} color="#333333" />
      ),
    sortingFn: sortingFns.basic,
    enableColumnFilter: false,
  },
  {
    accessorFn: (row) => row.role,
    id: "role",
    cell: (info) => {
      const role = info.getValue() as string;
      return role === "administrateur"
        ? "Admin"
        : role === "membre"
        ? "Rédacteur"
        : role === "observer"
        ? "Observateur"
        : "Client";
    },
    header: () => "Role",
    footer: (props) => props.column.id,
    enableColumnFilter: false,
  },
  {
    accessorKey: "username",
    header: "Nom / Prénom",
    cell: (row) => row.row.original.username,
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
  {
    accessorKey: "phoneNumber",
    header: "Téléphone",
    cell: (info) => info.getValue(),
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
  {
    accessorKey: "comment",
    header: "Commentaire",
    cell: (info) => info.getValue(),
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
  {
    accessorFn: (row) => row.team?.name ?? "",
    id: "team",
    header: "Équipe",
    cell: (info) => info.getValue() || "-",
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => (
      <span>
        {row.original.email}
        <br />
        {row.original.secoundEmail}
      </span>
    ),
    sortingFn: sortingFns.alphanumeric,
    enableColumnFilter: false,
  },
];

const UsersPlanning = () => {
  const [users, setUsers] = React.useState<UserDto[]>([]);
  const [displayedUsers, setDisplayedUsers] = React.useState<UserDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [teamModalOpened, { open: openTeamModal, close: closeTeamModal }] =
    useDisclosure(false);
  const [teamToDelete, setTeamToDelete] = useState<TeamDto | null>(null);
  const [
    deleteConfirmOpened,
    { open: openDeleteConfirm, close: closeDeleteConfirm },
  ] = useDisclosure(false);
  const api = useApi();

  const fetchTeams = async () => {
    try {
      const teamsData = await getTeams(api);
      setTeams(teamsData);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      await createTeam(api, newTeamName.trim());
      setNewTeamName("");
      fetchTeams();
      showNotification({
        message: "Équipe créée avec succès",
        color: "green",
      });
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.message || "Erreur lors de la création",
        color: "red",
      });
    }
  };

  const confirmDeleteTeam = (team: TeamDto) => {
    setTeamToDelete(team);
    openDeleteConfirm();
  };

  const handleDeleteTeam = async () => {
    if (!teamToDelete?.id) return;
    try {
      await deleteTeam(api, teamToDelete.id);
      fetchTeams();
      // Refresh users to update team assignments
      const response = await getUsers(api);
      setUsers(response);
      showNotification({
        message: "Équipe supprimée avec succès",
        color: "green",
      });
      closeDeleteConfirm();
      setTeamToDelete(null);
    } catch (error) {
      showNotification({
        message: "Erreur lors de la suppression",
        color: "red",
      });
    }
  };

  React.useEffect(() => {
    const fetchRecordings = async () => {
      const response = await getUsers(api);
      console.log(response);
      setUsers(response);
      setLoading(false);
    };
    fetchRecordings();
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  React.useEffect(() => {
    if (window.location.pathname === "/clients") {
      setDisplayedUsers(users.filter((user) => user.role === "client"));
    } else {
      setDisplayedUsers(users.filter((user) => user.role !== "client"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname, users]);

  const isRedactorsPage = window.location.pathname === "/redacteurs";

  return (
    <div>
      <HeaderAction planning />
      <Box p={30} bg="#F9FAFB">
        <Flex justify="space-between" align="center" mb={30}>
          <Title>
            {window.location.pathname === "/clients"
              ? "Données Clients"
              : "Données Rédacteurs"}
          </Title>
          {isRedactorsPage && (
            <Button onClick={openTeamModal} variant="outline">
              Gérer les équipes
            </Button>
          )}
        </Flex>
        <Box>
          <TableInit
            data={displayedUsers}
            setData={setUsers}
            loading={loading}
            columns={
              window.location.pathname === "/clients"
                ? columnsCustomers
                : columnsRedactors
            }
            redirect="user"
            // open={open}
            initialSorting={[{ id: "code", desc: false }]}
          />
        </Box>
      </Box>

      {/* Modal de gestion des équipes */}
      <Modal
        opened={teamModalOpened}
        onClose={closeTeamModal}
        title="Gestion des équipes"
        size="md"
      >
        <Flex gap={10} mb={20}>
          <TextInput
            placeholder="Nom de l'équipe"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateTeam();
            }}
          />
          <Button onClick={handleCreateTeam} leftIcon={<IconPlus size={16} />}>
            Créer
          </Button>
        </Flex>

        {teams.length === 0 ? (
          <Text color="dimmed" ta="center" py={20}>
            Aucune équipe créée
          </Text>
        ) : (
          <Box>
            {teams.map((team) => (
              <Flex
                key={team.id}
                justify="space-between"
                align="center"
                py={10}
                px={10}
                style={{ borderBottom: "1px solid #eee" }}
              >
                <Text>{team.name}</Text>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => confirmDeleteTeam(team)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Flex>
            ))}
          </Box>
        )}
      </Modal>

      {/* Modal de confirmation de suppression */}
      <Modal
        opened={deleteConfirmOpened}
        onClose={closeDeleteConfirm}
        title="Confirmer la suppression"
        size="sm"
      >
        <Text mb={20}>
          Êtes-vous sûr de vouloir supprimer l'équipe "{teamToDelete?.name}" ?
          Les rédacteurs assignés à cette équipe n'auront plus d'équipe.
        </Text>
        <Flex justify="flex-end" gap={10}>
          <Button variant="default" onClick={closeDeleteConfirm}>
            Annuler
          </Button>
          <Button color="red" onClick={handleDeleteTeam}>
            Supprimer
          </Button>
        </Flex>
      </Modal>
    </div>
  );
};

export default UsersPlanning;
